'use client';

import { useTranslations } from 'next-intl';
import { Eye, Pencil, Trash2, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import { RouteGate } from '@/components/route-gate';
import { ShippingPriorityTag } from './shipping-priority-tag';
import type { Shipment, ShipmentStatus } from '../types';

interface ShippingCompactRowProps {
  shipment: Shipment;
  onEdit: (shipment: Shipment) => void;
  onDelete: (shipment: Shipment) => void;
}

const statusStyles: Record<ShipmentStatus, string> = {
  draft:
    'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  ready_for_shipping:
    'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200',
  in_transit:
    'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
  received:
    'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
};

const statusDotColors: Record<ShipmentStatus, string> = {
  draft: 'bg-orange-500',
  ready_for_shipping: 'bg-green-500',
  in_transit: 'bg-blue-500',
  received: 'bg-teal-500'
};

const statusLabels: Record<ShipmentStatus, string> = {
  draft: 'statusDraft',
  ready_for_shipping: 'statusReadyForShipping',
  in_transit: 'statusInTransit',
  received: 'statusReceived'
};

export function ShippingCompactRow({
  shipment,
  onEdit,
  onDelete
}: ShippingCompactRowProps) {
  const t = useTranslations('shipping');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: shipment number + judicial entity */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-mono font-medium'>
            #{shipment.shipment_number}
          </span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {shipment.judicial_entity}
          </span>
        </div>
      </div>

      {/* Center: box count + priority */}
      <div className='hidden items-center gap-2 sm:flex'>
        <Badge variant='secondary' className='gap-1'>
          <Package className='h-3 w-3' />
          {shipment.box_count}
        </Badge>
        <ShippingPriorityTag priority={shipment.priority} />
      </div>

      {/* Right: status badge */}
      <div
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
          statusStyles[shipment.status]
        )}
      >
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
            statusDotColors[shipment.status]
          )}
        />
        {t(statusLabels[shipment.status])}
      </div>

      {/* Actions */}
      <div className='flex shrink-0 items-center gap-1'>
        <RouteGate route={`/dashboard/shipping/${shipment.id}`}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
            asChild
          >
            <Link href={`/dashboard/shipping/${shipment.id}`}>
              <Eye className='h-3.5 w-3.5' />
            </Link>
          </Button>
        </RouteGate>
        {shipment.status !== 'received' && (
          <PermissionGate permissions={['shipments.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
              onClick={() => onEdit(shipment)}
            >
              <Pencil className='h-3.5 w-3.5' />
            </Button>
          </PermissionGate>
        )}
        {shipment.status !== 'received' && (
          <PermissionGate permissions={['shipments.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
              onClick={() => onDelete(shipment)}
            >
              <Trash2 className='h-3.5 w-3.5' />
            </Button>
          </PermissionGate>
        )}
      </div>
    </div>
  );
}
